import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useVehicles } from '../hooks/useVehicles';
import { formatDate, formatCurrency } from '../utils/dateUtils';
import { ExpenseModal, EXPENSE_CATEGORIES } from '../components/common/ExpenseModal';
import { DollarSign, Plus, Download, Trash2, ExternalLink, Edit2, Camera, Search, CheckCircle, X } from 'lucide-react';
import { useOwner } from '../hooks/useOwner';
import toast from 'react-hot-toast';


const CATEGORIES = EXPENSE_CATEGORIES;
// Same vendor, different spelling or capitalisation, is one vendor when totalling spend.
const vendorKey = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
const catColors = { Fuel:'blue', Service:'green', Repairs:'orange', Insurance:'yellow', 'Road Tax':'yellow', Tyres:'gray', Cleaning:'gray', Parking:'gray', Fines:'red', Parts:'orange', Other:'gray', Mechanical:'orange' };

export const Expenses = () => {
  const { vehicles } = useVehicles();
  const [expenses, setExpenses] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [roadTax, setRoadTax] = useState([]);
  const [mechanical, setMechanical] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const { role, isOwner, isAdmin } = useOwner();
  const canEdit   = isOwner || isAdmin || role === 'editor';
  const canDelete = isOwner || isAdmin;
  const [catFilter, setCatFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [payFilter, setPayFilter] = useState('all');      // all | unpaid
  const [groupBy, setGroupBy] = useState('category');      // category | vendor | tag
  const [showAllGroups, setShowAllGroups] = useState(false);

  // The phone app's centre "Scan invoice" button opens this page with ?add=1.
  const quickOpened = useRef(false);
  useEffect(() => {
    if (quickOpened.current || !canEdit) return;
    if (new URLSearchParams(window.location.search).get('add') === '1') {
      quickOpened.current = true;
      setEditRecord(null);
      setShowModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [canEdit]);

  const fetch = useCallback(async () => {
    setLoading(true);
    const [e, i, r, m, s] = await Promise.all([
      supabase.from('expenses').select('*, vehicles(id,registration_number,brand,model)').order('expense_date', { ascending: false }),
      supabase.from('insurance_records').select('*, vehicles(id,registration_number,brand,model)'),
      supabase.from('road_tax_records').select('*, vehicles(id,registration_number,brand,model)'),
      supabase.from('mechanical_reports').select('*, vehicles(id,registration_number,brand,model)'),
      supabase.from('service_records').select('*, vehicles(id,registration_number,brand,model)'),
    ]);
    setExpenses(e.data || []);
    setInsurance(i.data || []);
    setRoadTax(r.data || []);
    setMechanical(m.data || []);
    setServices(s.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // Build combined list
  const allCosts = [
    ...expenses.map(r => ({
      id: `exp-${r.id}`,
      rawId: r.id,
      date: r.expense_date,
      category: r.category,
      description: r.description || '—',
      amount: Number(r.amount || 0),
      source: 'Manual',
      sourceColor: 'blue',
      supplier: r.supplier || '—',
      method: r.payment_method || '—',
      receipt_url: r.receipt_url,
      vehicle: r.vehicles,
      isManual: true,
      tags: r.tags || [],
      payment_status: r.payment_status || 'paid',
      payment_link: r.payment_link,
      due_date: r.due_date,
      invoice_number: r.invoice_number,
      notes: r.notes,
    })),
    ...insurance.filter(r => r.cost).map(r => ({
      id: `ins-${r.id}`,
      date: r.start_date || r.expiry_date,
      category: 'Insurance',
      description: r.insurance_company ? `Insurance — ${r.insurance_company}` : 'Insurance',
      amount: Number(r.cost || 0),
      source: 'Insurance',
      sourceColor: 'yellow',
      supplier: r.insurance_company || '—',
      method: '—',
      receipt_url: r.document_url,
      vehicle: r.vehicles,
      isManual: false,
    })),
    ...roadTax.filter(r => r.amount_paid).map(r => ({
      id: `rt-${r.id}`,
      date: r.payment_date || r.expiry_date,
      category: 'Road Tax',
      description: 'Road Tax',
      amount: Number(r.amount_paid || 0),
      source: 'Road Tax',
      sourceColor: 'yellow',
      supplier: '—',
      method: '—',
      receipt_url: r.receipt_url,
      vehicle: r.vehicles,
      isManual: false,
    })),
    ...mechanical.filter(r => r.cost).map(r => ({
      id: `mech-${r.id}`,
      date: r.report_date,
      category: 'Mechanical',
      description: r.garage_name ? `Mechanical — ${r.garage_name}` : 'Mechanical Report',
      amount: Number(r.cost || 0),
      source: 'Mechanical',
      sourceColor: 'orange',
      supplier: r.garage_name || '—',
      method: '—',
      receipt_url: r.document_url,
      vehicle: r.vehicles,
      isManual: false,
    })),
    ...services.filter(r => r.cost).map(r => ({
      id: `svc-${r.id}`,
      date: r.service_date,
      category: 'Service',
      description: r.garage_name ? `Service — ${r.garage_name}` : 'Service',
      amount: Number(r.cost || 0),
      source: 'Service',
      sourceColor: 'green',
      supplier: r.garage_name || '—',
      method: '—',
      receipt_url: r.invoice_url,
      vehicle: r.vehicles,
      isManual: false,
    })),
  ].sort((a, b) => {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(b.date) - new Date(a.date);
  });

  // Apply filters
  const filtered = allCosts.filter(r => {
    const matchCat = !catFilter || r.category === catFilter;
    const matchVehicle = !vehicleFilter || r.vehicle?.id === vehicleFilter;
    const matchSource = sourceFilter === 'all' || r.source === sourceFilter;
    const matchYear = !yearFilter || (r.date && r.date.startsWith(yearFilter));
    const matchFrom = !dateFrom || (r.date && r.date >= dateFrom);
    const matchTo = !dateTo || (r.date && r.date <= dateTo);
    const matchTag = !tagFilter || (r.tags || []).includes(tagFilter);
    const matchVendor = !vendorFilter || vendorKey(r.supplier) === vendorFilter;
    const matchPay = payFilter === 'all' || r.payment_status === 'unpaid';
    const q = search.trim().toLowerCase();
    const matchSearch = !q || [
      r.description, r.supplier, r.category, r.invoice_number, r.notes,
      r.vehicle?.registration_number, r.vehicle?.brand, r.vehicle?.model, ...(r.tags || []),
    ].some(x => String(x || '').toLowerCase().includes(q));
    return matchCat && matchVehicle && matchSource && matchYear && matchFrom && matchTo && matchTag && matchVendor && matchPay && matchSearch;
  });

  const resetFilters = () => {
    setCatFilter('');
    setVehicleFilter('');
    setSourceFilter('all');
    setYearFilter('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setTagFilter('');
    setVendorFilter('');
    setPayFilter('all');
  };

  const hasFilters = catFilter || vehicleFilter || sourceFilter !== 'all' || yearFilter || dateFrom || dateTo || search || tagFilter || vendorFilter || payFilter !== 'all';

  const grandTotal = filtered.reduce((s, r) => s + r.amount, 0);

  // Breakdown of the CURRENT results by category, vendor or tag. Chips are clickable and
  // narrow the list to that group. An expense with several tags counts under each of them.
  const groups = (() => {
    const acc = new Map();   // key -> { label, amount, count }
    const add = (key, label, amount) => {
      const cur = acc.get(key) || { label, amount: 0, count: 0 };
      cur.amount += amount; cur.count += 1;
      acc.set(key, cur);
    };
    filtered.forEach(r => {
      if (groupBy === 'category') add(r.category, r.category, r.amount);
      else if (groupBy === 'vendor') { const k = vendorKey(r.supplier === '—' ? '' : r.supplier); add(k || '(no vendor)', k ? r.supplier : '(no vendor)', r.amount); }
      else if ((r.tags || []).length) r.tags.forEach(t => add(t, t, r.amount));
      else add('(untagged)', '(untagged)', r.amount);
    });
    return [...acc.entries()].map(([key, v]) => ({ key, ...v })).sort((a, b) => b.amount - a.amount);
  })();
  const visibleGroups = showAllGroups ? groups : groups.slice(0, 14);
  const unpaid = allCosts.filter(r => r.payment_status === 'unpaid');
  const unpaidTotal = unpaid.reduce((s, r) => s + r.amount, 0);
  const pickGroup = (g) => {
    if (groupBy === 'category') setCatFilter(c => (c === g.key ? '' : g.key));
    else if (groupBy === 'vendor') { if (g.key !== '(no vendor)') setVendorFilter(v => (v === g.key ? '' : g.key)); }
    else if (g.key !== '(untagged)') setTagFilter(t => (t === g.key ? '' : g.key));
  };
  const groupActive = (g) => (groupBy === 'category' ? catFilter === g.key : groupBy === 'vendor' ? vendorFilter === g.key : tagFilter === g.key);

  const markPaid = async (id) => {
    const { error } = await supabase.from('expenses').update({ payment_status: 'paid', paid_at: new Date().toISOString() }).eq('id', id);
    if (error) { toast.error('Could not update: ' + error.message); return; }
    toast.success('Marked as paid');
    fetch();
  };

  const exportCSV = () => {
    const rows = [
      ['Date', 'Vehicle', 'Source', 'Category', 'Description', 'Amount', 'Supplier', 'Method', 'Tags', 'Payment', 'Invoice no.'],
      ...filtered.map(r => [
        r.date,
        r.vehicle?.registration_number,
        r.source,
        r.category,
        r.description,
        r.amount,
        r.supplier,
        r.method,
        (r.tags || []).join('; '),
        r.payment_status === 'unpaid' ? 'Unpaid' : 'Paid',
        r.invoice_number || '',
      ])
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fleet-expenses.csv';
    a.click();
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    const { error } = await supabase.from('expenses').delete().eq('id', id);
    if (error) { toast.error('Failed to delete'); return; }
    toast.success('Expense deleted');
    fetch();
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Expenses</div>
          <div className="page-subtitle">
            {hasFilters ? 'Total for these results' : 'Grand Total'}: {formatCurrency(grandTotal)} — {filtered.length} records
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={exportCSV}><Download size={14} /> Export CSV</button>
          {canEdit && (
            <button className="btn btn-ghost" onClick={() => { setEditRecord(null); setShowModal(true); }}>
              <Camera size={14} /> Scan Invoice
            </button>
          )}
          {canEdit && (
            <button className="btn btn-primary" onClick={() => { setEditRecord(null); setShowModal(true); }}>
              <Plus size={14} /> Add Expense
            </button>
          )}
        </div>
      </div>

      {/* Invoices waiting to be paid */}
      {unpaid.length > 0 && (
        <div style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid rgba(249,115,22,0.4)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#f97316' }}>
            <strong>{unpaid.length}</strong> invoice{unpaid.length !== 1 ? 's' : ''} awaiting payment — <strong>{formatCurrency(unpaidTotal)}</strong>
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setPayFilter(f => (f === 'unpaid' ? 'all' : 'unpaid'))}>
            {payFilter === 'unpaid' ? 'Show all expenses' : 'Show unpaid only'}
          </button>
        </div>
      )}

      {/* Where the money went — by category, vendor or tag */}
      {groups.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>Spend by</span>
            {[['category', 'Category'], ['vendor', 'Vendor'], ['tag', 'Tag']].map(([k, label]) => (
              <button key={k} onClick={() => { setGroupBy(k); setShowAllGroups(false); }}
                style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  border: `1px solid ${groupBy === k ? '#4f8ef7' : 'var(--border)'}`,
                  background: groupBy === k ? '#4f8ef722' : 'var(--surface)',
                  color: groupBy === k ? '#4f8ef7' : 'var(--text3)',
                }}>{label}</button>
            ))}
            {groupBy === 'tag' && <span style={{ fontSize: 11, color: 'var(--text3)' }}>An expense with several tags counts under each tag.</span>}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {visibleGroups.map(g => (
              <div key={g.key} onClick={() => pickGroup(g)}
                title={`${g.count} expense${g.count !== 1 ? 's' : ''} — click to filter`}
                style={{
                  background: groupActive(g) ? 'rgba(79,142,247,0.12)' : 'var(--surface)',
                  border: `1px solid ${groupActive(g) ? '#4f8ef7' : 'var(--border)'}`,
                  borderRadius: 8, padding: '10px 16px', minWidth: 120, cursor: 'pointer',
                }}>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.label}</div>
                <div style={{ fontWeight: 700, color: 'var(--orange)' }}>{formatCurrency(g.amount)}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{g.count} expense{g.count !== 1 ? 's' : ''}</div>
              </div>
            ))}
            {groups.length > 14 && (
              <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'center' }} onClick={() => setShowAllGroups(x => !x)}>
                {showAllGroups ? 'Show fewer' : `Show all ${groups.length}`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20, padding: '14px 16px' }}>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
          <input
            className="form-input"
            style={{ paddingLeft: 36 }}
            placeholder="Search spend: a vendor, tyres, brake pads, an invoice number, a car…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {(tagFilter || vendorFilter) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {tagFilter && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: 'rgba(79,142,247,0.14)', color: 'var(--accent)' }}>
                Tag: {tagFilter}
                <button onClick={() => setTagFilter('')} aria-label="Clear tag filter" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={12} /></button>
              </span>
            )}
            {vendorFilter && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: 'rgba(79,142,247,0.14)', color: 'var(--accent)' }}>
                Vendor: {vendorFilter}
                <button onClick={() => setVendorFilter('')} aria-label="Clear vendor filter" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={12} /></button>
              </span>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-input" style={{ width: 'auto' }} value={vehicleFilter} onChange={e => setVehicleFilter(e.target.value)}>
            <option value="">All vehicles</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>{v.registration_number} — {v.brand} {v.model}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { value: 'all', label: 'All', color: '#4f8ef7' },
              { value: 'Manual', label: 'Manual', color: '#4f8ef7' },
              { value: 'Insurance', label: 'Insurance', color: '#eab308' },
              { value: 'Road Tax', label: 'Road Tax', color: '#eab308' },
              { value: 'Mechanical', label: 'Mechanical', color: '#f97316' },
              { value: 'Service', label: 'Service', color: '#22c55e' },
            ].map(s => (
              <button
                key={s.value}
                onClick={() => setSourceFilter(s.value)}
                style={{
                  padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
                  border: `1px solid ${sourceFilter === s.value ? s.color : 'var(--border)'}`,
                  background: sourceFilter === s.value ? `${s.color}22` : 'var(--surface)',
                  color: sourceFilter === s.value ? s.color : 'var(--text3)',
                  boxShadow: sourceFilter === s.value ? `0 4px 12px ${s.color}33` : 'none',
                  transform: sourceFilter === s.value ? 'translateY(-1px)' : 'none',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
          <select className="form-input" style={{ width: 'auto' }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select className="form-input" style={{ width: 'auto' }} value={yearFilter} onChange={e => { setYearFilter(e.target.value); setDateFrom(''); setDateTo(''); }}>
            <option value="">All years</option>
            {Array.from({ length: new Date().getFullYear() - 2019 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, color: 'var(--text2)', whiteSpace: 'nowrap' }}>Custom period:</span>
            <input
              className="form-input"
              type="date"
              style={{ width: 'auto' }}
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setYearFilter(''); }}
              placeholder="From"
            />
            <span style={{ fontSize: 14, color: 'var(--text2)' }}>to</span>
            <input
              className="form-input"
              type="date"
              style={{ width: 'auto' }}
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setYearFilter(''); }}
              placeholder="To"
            />
          </div>
          {hasFilters && (
            <button className="btn btn-ghost btn-sm" onClick={resetFilters}>
              ✕ Reset filters
            </button>
          )}
        </div>
      </div>
      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <DollarSign />
            <h3>No expenses found</h3>
            <p>Add a manual expense or add costs to your insurance, road tax, mechanical or service records.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vehicle</th>
                  <th>Source</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Tags</th>
                  <th>Amount</th>
                  <th>Supplier</th>
                  <th>Receipt</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td>{formatDate(r.date)}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: 1, textTransform: 'uppercase' }}>
                          {r.vehicle?.registration_number || r.vehicles?.registration_number}
                        </span>
                        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 500, color: 'var(--text2)' }}>
                          {r.vehicle?.brand || r.vehicles?.brand} {r.vehicle?.model || r.vehicles?.model}
                        </span>
                      </div>
                    </td>
                    <td><span className={`badge badge-${r.sourceColor}`}>{r.source}</span></td>
                    <td><span className={`badge badge-${catColors[r.category] || 'gray'}`}>{r.category}</span></td>
                    <td>{r.description}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 160 }}>
                        {(r.tags || []).map(t => (
                          <span key={t} onClick={() => setTagFilter(t)} style={{ cursor: 'pointer', padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(79,142,247,0.14)', color: 'var(--accent)' }}>{t}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ color: 'var(--orange)', fontWeight: 700 }}>
                      {formatCurrency(r.amount)}
                      {r.payment_status === 'unpaid' && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#f97316', marginTop: 2 }}>
                          UNPAID{r.due_date ? ` · by ${formatDate(r.due_date)}` : ''}
                        </div>
                      )}
                    </td>
                    <td>{r.supplier}</td>
                    <td>
                      {r.receipt_url && (
                        <a href={r.receipt_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </td>
                    <td>
                      {r.isManual ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          {r.payment_status === 'unpaid' && r.payment_link && (
                            <a href={r.payment_link} target="_blank" rel="noreferrer noopener" className="btn btn-primary btn-sm">Pay</a>
                          )}
                          {r.payment_status === 'unpaid' && canEdit && (
                            <button className="btn btn-ghost btn-sm" title="Mark as paid" onClick={() => markPaid(r.rawId)}><CheckCircle size={12} /></button>
                          )}
                          {canEdit && (
                            <button className="btn btn-ghost btn-sm" onClick={() => {
                              setEditRecord(expenses.find(e => e.id === r.rawId));
                              setShowModal(true);
                            }}>
                              <Edit2 size={12} />
                            </button>
                          )}
                          {canDelete && (
                            <button className="btn btn-danger btn-sm" onClick={() => deleteExpense(r.rawId)}>
                              <Trash2 size={11} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 13, color: 'var(--text2)' }}>Auto</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <ExpenseModal
          vehicles={vehicles}
          record={editRecord}
          onClose={() => {
            setShowModal(false);
            setEditRecord(null);
            fetch();
          }}
        />
      )}
    </div>
  );
};
